import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { getTenantConfig } from '@/lib/tenant-config'
import { Button, Input, Checkbox } from '@lead-magnifier/ui'
import {
  CheckCircle2, Loader2, ArrowRight, Lock, Mail, Phone, User,
  Globe, Download, FileText, Users, FolderDown, MessageSquare,
} from 'lucide-react'

export const Route = createFileRoute('/s/$tenantSlug/$eventSlug/$exhibitorSlug/')({
  component: ScanPage,
})

interface ExhibitorData {
  eventExhibitorId: string
  eventId: string
  eventName: string
  companyName: string
  companyDescription: string | null
  logoUrl: string | null
  bannerUrl: string | null
  websiteUrl: string | null
  personalMessage: string | null
  contactPersons: Array<{ name: string; role?: string; email?: string; phone?: string; photo_url?: string }>
  documents: Array<{ name: string; url: string; type?: string }>
  primaryColor: string
  backgroundColor: string
}

type PageState = 'loading' | 'ask-email' | 'pending-doi' | 'unlocked' | 'already' | 'error'

const VISITOR_TOKEN_PREFIX = 'messetasche-'
function getStoredToken(s: string) { return localStorage.getItem(`${VISITOR_TOKEN_PREFIX}${s}`) }
function storeToken(s: string, t: string) { localStorage.setItem(`${VISITOR_TOKEN_PREFIX}${s}`, t) }

function ScanPage() {
  const { tenantSlug, eventSlug, exhibitorSlug } = Route.useParams()
  const tenant = getTenantConfig()
  const [state, setState] = useState<PageState>('loading')
  const [exhibitor, setExhibitor] = useState<ExhibitorData | null>(null)
  const [visitorEmail, setVisitorEmail] = useState('')
  const [visitorName, setVisitorName] = useState('')
  const [consent, setConsent] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [scanCount, setScanCount] = useState(0)
  const [visitorToken, setVisitorToken] = useState<string | null>(null)
  const [errorMsg, setErrorMsg] = useState('')

  useEffect(() => { load() }, [tenantSlug, eventSlug, exhibitorSlug])

  async function load() {
    try {
      const { data: ev } = await supabase.from('events')
        .select('id, name, primary_color, background_color, tenants!inner(slug)')
        .eq('slug', eventSlug).single()
      if (!ev) { setErrorMsg('Event nicht gefunden'); setState('error'); return }

      const { data: prof } = await supabase.from('exhibitor_profiles')
        .select('*').eq('slug', exhibitorSlug).maybeSingle()
      if (!prof) { setErrorMsg('Aussteller nicht gefunden'); setState('error'); return }

      const { data: ee } = await supabase.from('event_exhibitors')
        .select('id').eq('event_id', ev.id).eq('user_id', prof.user_id).maybeSingle()
      if (!ee) { setErrorMsg('Aussteller nicht zugeordnet'); setState('error'); return }

      setExhibitor({
        eventExhibitorId: ee.id, eventId: ev.id, eventName: ev.name,
        companyName: prof.company_name || 'Aussteller', companyDescription: prof.company_description,
        logoUrl: prof.logo_url, bannerUrl: prof.banner_url, websiteUrl: prof.website_url,
        personalMessage: prof.personal_message,
        contactPersons: (prof.contact_persons as ExhibitorData['contactPersons']) || [],
        documents: (prof.documents as ExhibitorData['documents']) || [],
        primaryColor: ev.primary_color || '#3b82f6', backgroundColor: ev.background_color || '#0F2B47',
      })

      const stored = getStoredToken(eventSlug)
      if (stored) {
        const { data: v } = await supabase.from('visitors')
          .select('id, name, doi_confirmed').eq('token', stored).maybeSingle()
        if (v) {
          setVisitorToken(stored); setVisitorName(v.name || '')
          const { data: scan } = await supabase.from('visitor_scans')
            .select('id').eq('visitor_id', v.id).eq('event_exhibitor_id', ee.id).maybeSingle()
          if (!scan) await scanOnly(v.id)
          const { count } = await supabase.from('visitor_scans')
            .select('id', { count: 'exact', head: true }).eq('visitor_id', v.id)
          setScanCount(count || 0)
          setState(scan ? (v.doi_confirmed ? 'already' : 'pending-doi') : (v.doi_confirmed ? 'unlocked' : 'pending-doi'))
          return
        }
      }
      setState('ask-email')
    } catch { setErrorMsg('Fehler'); setState('error') }
  }

  async function scanOnly(vid: string) {
    if (!exhibitor) return
    const { error } = await supabase.from('visitor_scans')
      .insert({ visitor_id: vid, event_exhibitor_id: exhibitor.eventExhibitorId })
    if (error && error.code !== '23505') throw error
  }

  async function handleSubmit() {
    if (!visitorEmail || !consent || !exhibitor) return
    setSubmitting(true)
    try {
      const token = crypto.randomUUID()
      const doiToken = crypto.randomUUID()
      const { data: v, error: err } = await supabase.from('visitors')
        .insert({ event_id: exhibitor.eventId, email: visitorEmail.trim(), name: visitorName.trim() || null, token, doi_token: doiToken })
        .select('id').single()
      if (err) {
        if (err.code === '23505') {
          const { data: ex } = await supabase.from('visitors')
            .select('id, token').eq('event_id', exhibitor.eventId).eq('email', visitorEmail.trim()).single()
          if (ex) { storeToken(eventSlug, ex.token); setVisitorToken(ex.token); await scanOnly(ex.id)
            const { count } = await supabase.from('visitor_scans').select('id', { count: 'exact', head: true }).eq('visitor_id', ex.id)
            setScanCount(count || 0); setState('pending-doi'); return }
        }
        throw err
      }
      storeToken(eventSlug, token); setVisitorToken(token); await scanOnly(v.id)
      const { count } = await supabase.from('visitor_scans').select('id', { count: 'exact', head: true }).eq('visitor_id', v.id)
      setScanCount(count || 0); setState('pending-doi')
    } catch (e) { setErrorMsg(e instanceof Error ? e.message : 'Fehler'); setState('error') }
    finally { setSubmitting(false) }
  }

  const messetascheUrl = visitorToken ? `${window.location.origin}/messetasche/${visitorToken}` : ''
  const brandColor = exhibitor?.primaryColor || '#1B4B7A'
  const isUnlocked = state === 'unlocked' || state === 'already'
  const contactCount = exhibitor?.contactPersons.length || 0
  const docCount = exhibitor?.documents.length || 0

  if (state === 'loading') return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-900">
      <div className="h-8 w-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
    </div>
  )

  if (state === 'error') return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-neutral-900">
      <div className="text-center text-white">
        <p className="text-lg font-semibold">Etwas ist schiefgelaufen</p>
        <p className="text-white/60 mt-1">{errorMsg}</p>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-neutral-100">

      {/* ── Hero Banner ──────────────────── */}
      <div className="relative h-52 sm:h-64 overflow-hidden">
        {exhibitor?.bannerUrl ? (
          <img src={exhibitor.bannerUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0" style={{ backgroundColor: exhibitor?.backgroundColor }} />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />

        {/* Logo + Name overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6">
          <div className="max-w-md mx-auto flex items-end gap-4">
            {exhibitor?.logoUrl && (
              <img src={exhibitor.logoUrl} alt="" className="h-14 w-14 rounded-md object-contain bg-white p-1 shadow-lg shrink-0" />
            )}
            <div className="text-white min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold leading-tight drop-shadow-lg">{exhibitor?.companyName}</h1>
              <p className="text-white/70 text-sm mt-0.5">{exhibitor?.eventName}</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Content Card ─────────────────── */}
      <div className="px-4 -mt-4 pb-8 relative z-10">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg overflow-hidden">

          {/* ── ASK EMAIL ────────────────── */}
          {state === 'ask-email' && (
            <div className="p-6">
              <h2 className="text-xl font-bold text-center">
                Alle Unterlagen von {exhibitor?.companyName} mitnehmen
              </h2>
              <p className="text-center text-neutral-500 mt-2 text-sm">
                Geben Sie Ihre E-Mail ein und erhalten Sie Kontaktdaten, Dokumente und persönliche Angebote.
              </p>

              {/* What you get — visual */}
              <div className="grid grid-cols-3 gap-3 mt-6 mb-6">
                <div className="text-center p-3 rounded-md bg-neutral-50">
                  <Users className="h-6 w-6 mx-auto mb-1.5" style={{ color: brandColor }} />
                  <p className="text-xs font-semibold">{contactCount} Kontakt{contactCount !== 1 ? 'e' : ''}</p>
                </div>
                <div className="text-center p-3 rounded-md bg-neutral-50">
                  <FolderDown className="h-6 w-6 mx-auto mb-1.5" style={{ color: brandColor }} />
                  <p className="text-xs font-semibold">{docCount} Download{docCount !== 1 ? 's' : ''}</p>
                </div>
                <div className="text-center p-3 rounded-md bg-neutral-50">
                  <MessageSquare className="h-6 w-6 mx-auto mb-1.5" style={{ color: brandColor }} />
                  <p className="text-xs font-semibold">Nachricht</p>
                </div>
              </div>

              {/* Email Input */}
              <Input
                type="email"
                value={visitorEmail}
                onChange={(e) => setVisitorEmail(e.target.value)}
                placeholder="Ihre E-Mail-Adresse"
                className="h-14 text-lg text-center"
                autoFocus
              />

              <Input
                value={visitorName}
                onChange={(e) => setVisitorName(e.target.value)}
                placeholder="Ihr Name (optional)"
                className="h-12 mt-2"
              />

              <div className="flex items-start gap-2.5 mt-4">
                <Checkbox id="consent" checked={consent} onCheckedChange={(c) => setConsent(c === true)} className="mt-0.5" />
                <label htmlFor="consent" className="text-xs text-neutral-500 leading-relaxed">
                  Ja, ich möchte die Unterlagen erhalten. Meine E-Mail wird an den Aussteller weitergegeben.
                </label>
              </div>

              <Button
                className="w-full h-14 text-base font-bold mt-4 rounded-md"
                style={{ backgroundColor: brandColor }}
                onClick={handleSubmit}
                disabled={!visitorEmail || !consent || submitting}
              >
                {submitting ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : null}
                Jetzt freischalten
              </Button>

              <p className="text-center text-xs text-neutral-400 mt-3">
                Gilt für alle Aussteller auf dieser Messe — nur einmal eingeben.
              </p>
            </div>
          )}

          {/* ── PENDING DOI ──────────────── */}
          {state === 'pending-doi' && (
            <div className="p-6 text-center">
              <div className="h-16 w-16 rounded-md mx-auto mb-4 flex items-center justify-center bg-green-50">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-xl font-bold">Gespeichert!</h2>
              <p className="text-neutral-500 mt-2">
                <strong className="text-neutral-800">{exhibitor?.companyName}</strong> ist jetzt in Ihrer Messetasche.
                {scanCount > 1 && <span className="text-neutral-400"> ({scanCount} Aussteller)</span>}
              </p>

              <div className="mt-5 p-4 rounded-md bg-amber-50 border border-amber-100 text-left">
                <p className="text-sm font-semibold text-amber-800 flex items-center gap-2">
                  <Mail className="h-4 w-4" /> E-Mail bestätigen
                </p>
                <p className="text-sm text-amber-700 mt-1">
                  Wir haben Ihnen eine Bestätigungs-E-Mail gesendet. Nach der Bestätigung werden alle Kontaktdaten und Dokumente freigeschaltet.
                </p>
                <p className="text-xs text-amber-600 mt-2">
                  Sie können die Bestätigung auch später von zu Hause machen.
                </p>
              </div>

              <div className="mt-5 space-y-2">
                <button className="text-sm text-neutral-500 hover:text-neutral-700 underline"
                  onClick={() => { window.alert('Bestätigungs-E-Mail wird erneut gesendet (sobald E-Mail-Versand eingerichtet ist).') }}>
                  E-Mail nicht erhalten? Erneut senden
                </button>
                <br />
                <button className="text-xs text-neutral-400 hover:text-neutral-600"
                  onClick={() => { localStorage.removeItem(`${VISITOR_TOKEN_PREFIX}${eventSlug}`); setVisitorToken(null); setVisitorEmail(''); setConsent(false); setState('ask-email') }}>
                  E-Mail-Adresse ändern
                </button>
              </div>

              {messetascheUrl && (
                <a href={messetascheUrl} className="block mt-6">
                  <Button className="w-full h-12 rounded-md font-bold" style={{ backgroundColor: brandColor }}>
                    Weitere Aussteller scannen
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </a>
              )}
            </div>
          )}

          {/* ── UNLOCKED ─────────────────── */}
          {isUnlocked && (
            <div className="animate-fade-in-up">
              <div className="p-5 text-center" style={{ backgroundColor: brandColor + '08' }}>
                <CheckCircle2 className="h-8 w-8 mx-auto mb-1" style={{ color: brandColor }} />
                <p className="font-bold text-lg">{state === 'already' ? 'Bereits gespeichert' : 'Freigeschaltet!'}</p>
                <p className="text-sm text-neutral-500">{scanCount} Aussteller in Ihrer Messetasche</p>
              </div>

              {exhibitor?.companyDescription && (
                <div className="px-6 pt-5">
                  <p className="text-neutral-600 leading-relaxed">{exhibitor.companyDescription}</p>
                  {exhibitor.websiteUrl && (
                    <a href={exhibitor.websiteUrl} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm mt-2" style={{ color: brandColor }}>
                      <Globe className="h-4 w-4" />Website besuchen
                    </a>
                  )}
                </div>
              )}

              {exhibitor?.personalMessage && (
                <div className="mx-6 mt-4 p-4 rounded-md border-l-2" style={{ backgroundColor: brandColor + '06', borderLeftColor: brandColor }}>
                  <p className="text-sm text-neutral-700 leading-relaxed">{exhibitor.personalMessage}</p>
                </div>
              )}

              {exhibitor && exhibitor.contactPersons.length > 0 && (
                <div className="px-6 pt-5 pb-2">
                  <p className="text-xs font-bold text-neutral-900 uppercase tracking-wider mb-4">Ihre Ansprechpartner</p>
                  <div className="grid grid-cols-2 gap-3">
                    {exhibitor.contactPersons.map((cp, i) => (
                      <div key={i} className="bg-neutral-50 rounded-md p-4 text-center">
                        {cp.photo_url ? (
                          <img src={cp.photo_url} alt={cp.name} className="h-20 w-20 rounded-md object-cover mx-auto mb-3" />
                        ) : (
                          <div className="h-20 w-20 rounded-md bg-neutral-200 flex items-center justify-center mx-auto mb-3">
                            <User className="h-8 w-8 text-neutral-400" />
                          </div>
                        )}
                        <p className="font-bold text-sm">{cp.name}</p>
                        {cp.role && <p className="text-xs text-neutral-500 mt-0.5">{cp.role}</p>}
                        <div className="flex flex-col gap-1.5 mt-3">
                          {cp.email && (
                            <a href={`mailto:${cp.email}`} className="flex items-center justify-center gap-1.5 px-3 py-2 rounded text-xs font-semibold text-white" style={{ backgroundColor: brandColor }}>
                              <Mail className="h-3.5 w-3.5" />E-Mail
                            </a>
                          )}
                          {cp.phone && (
                            <a href={`tel:${cp.phone}`} className="flex items-center justify-center gap-1.5 px-3 py-2 rounded border border-neutral-200 text-xs font-semibold text-neutral-700">
                              <Phone className="h-3.5 w-3.5" />Anrufen
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {exhibitor && exhibitor.documents.length > 0 && (
                <div className="px-6 pt-4 pb-2">
                  <p className="text-xs font-bold text-neutral-900 uppercase tracking-wider mb-3">Downloads</p>
                  {exhibitor.documents.map((doc, i) => (
                    <a key={i} href={doc.url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 rounded-md bg-neutral-50 border border-neutral-100 hover:bg-neutral-100 transition-colors mb-2 group">
                      <div className="h-10 w-10 rounded-md flex items-center justify-center" style={{ backgroundColor: brandColor + '10' }}>
                        <FileText className="h-5 w-5" style={{ color: brandColor }} />
                      </div>
                      <div className="flex-1"><p className="text-sm font-semibold">{doc.name}</p><p className="text-xs text-neutral-400 uppercase">{doc.type || 'PDF'}</p></div>
                      <Download className="h-4 w-4 text-neutral-300 group-hover:text-neutral-600 shrink-0" />
                    </a>
                  ))}
                </div>
              )}

              {messetascheUrl && (
                <div className="p-6 pt-4">
                  <a href={messetascheUrl}>
                    <Button className="w-full h-12 rounded-md font-bold" variant="outline">
                      Alle Aussteller ansehen <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </a>
                </div>
              )}
            </div>
          )}
        </div>
        <p className="text-center text-xs text-neutral-400 mt-6">{tenant.branding.name}</p>
      </div>
    </div>
  )
}
